import { STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { localStorageUtil } from '@/shared/utils/local-storage';

/**
 * Client-side bearer token for the dashboard auth path.
 *
 * The API mints a signed JWT on `/auth/login`; the dashboard persists it via
 * `setAuthToken` and the axios interceptor attaches it on every request as
 * `Authorization: Bearer <token>`.
 *
 * The legacy "Continue as Demo" path has no server round-trip; it falls
 * back to `generateAuthToken()` which mints a random opaque UUID just so
 * the request envelope stays production-shaped. Do not treat that fallback
 * as a JWT.
 */

/** Persists a server-issued JWT (or any bearer string). */
export function setAuthToken(token: string): void {
    localStorageUtil.set(STORAGE_KEYS.AUTH_TOKEN, token);
}

/** Demo-only: mint a random opaque token when there's no server login. */
export function generateAuthToken(): string {
    const token = newOpaqueToken();
    localStorageUtil.set(STORAGE_KEYS.AUTH_TOKEN, token);
    return token;
}

/** Reads the current token from storage, or `null` if none. Used by the axios bearer provider. */
export function readAuthToken(): string | null {
    return localStorageUtil.get<string>(STORAGE_KEYS.AUTH_TOKEN);
}

/** Removes the token. Called by sign-out / session cleanup. */
export function clearAuthToken(): void {
    localStorageUtil.remove(STORAGE_KEYS.AUTH_TOKEN);
}

function newOpaqueToken(): string {
    // crypto.randomUUID is available in every browser we target (Chrome 92+,
    // Safari 15.4+, Firefox 95+). No fallback needed for the demo path.
    return globalThis.crypto.randomUUID();
}
